import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import axios from 'axios';
import './AddPackageModal.css';
import cacheStorage from '../../../utils/cacheStorage';

export default function AddPackageModal({ isOpen, onClose, onAddSuccess, editData }) {
  const [formData, setFormData] = useState({
    tracking_code: '',
    status: 'Pending',
    courier_id: { Int64: 0, Valid: false },
    total_weight: '',
    total_volume: '',
    date_start: { String: '', Valid: false },
    date_end: { String: '', Valid: false },
    delivery_address: { String: '', Valid: false },
    customer_name: { String: '', Valid: false },
    customer_phone: { String: '', Valid: false }
  });

  useEffect(() => {
    if (editData) {
      setFormData({
        tracking_code: editData.tracking_code || '',
        status: editData.status || 'Pending',
        courier_id: editData.courier_id || { Int64: 0, Valid: false },
        total_weight: editData.total_weight || '',
        total_volume: editData.total_volume || '',
        date_start: editData.date_start || { String: '', Valid: false },
        date_end: editData.date_end || { String: '', Valid: false },
        delivery_address: editData.delivery_address || { String: '', Valid: false },
        customer_name: editData.customer_name || { String: '', Valid: false },
        customer_phone: editData.customer_phone || { String: '', Valid: false }
      });
    } else {
      setFormData({
        tracking_code: '',
        status: 'Pending',
        courier_id: { Int64: 0, Valid: false },
        total_weight: '',
        total_volume: '',
        date_start: { String: '', Valid: false },
        date_end: { String: '', Valid: false },
        delivery_address: { String: '', Valid: false },
        customer_name: { String: '', Valid: false },
        customer_phone: { String: '', Valid: false }
      });
    }
  }, [editData, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (['delivery_address', 'customer_name', 'customer_phone', 'date_start', 'date_end'].includes(name)) {
        setFormData(prev => ({ ...prev, [name]: { String: value, Valid: value !== '' } }));
    } else if (name === 'courier_id') {
        const num = parseInt(value, 10);
        setFormData(prev => ({ ...prev, [name]: { Int64: isNaN(num) ? 0 : num, Valid: !isNaN(num) && value !== '' } }));
    } else if (name === 'total_weight' || name === 'total_volume') {
        setFormData(prev => ({ ...prev, [name]: value ? parseFloat(value) : 0 }));
    } else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tracking_code || !formData.status) {
      alert("Please fill required fields (Tracking Code & Status).");
      return;
    }

    // Get the stored access token
    const token = cacheStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    try {
      if (editData && editData.id) {
        await axios.put(`/api/packages/${editData.id}`, formData, { headers });
      } else {
        await axios.post('/api/packages', formData, { headers });
      }
      onAddSuccess();
    } catch (err) {
      console.error("Failed to save package:", err);
      // Show the actual server error message
      const serverMsg = err.response?.data || err.message;
      alert("Server error: " + serverMsg);
    }
  };

  return (
    <div className="package-modal-overlay">
      <div className="package-modal-content">
        <div className="package-modal-header">
          <h2>Add Package</h2>
          <button className="package-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="package-modal-form">
          <div className="package-form-group">
            <label>ID <span className="required">*</span></label>
            <input
              name="tracking_code"
              value={formData.tracking_code}
              onChange={handleChange}
              required
              placeholder="10001"
            />
          </div>

          <div className="package-form-row">
            <div className="package-form-group half">
              <label>Customer Name</label>
              <input name="customer_name" value={formData.customer_name?.String || ''} onChange={handleChange} />
            </div>
            <div className="package-form-group half">
              <label>Customer Phone</label>
              <input name="customer_phone" value={formData.customer_phone?.String || ''} onChange={handleChange} />
            </div>
          </div>

          <div className="package-form-group">
            <label>Delivery Address</label>
            <input name="delivery_address" value={formData.delivery_address?.String || ''} onChange={handleChange} placeholder="Nizami küç. 123, Bakı" />
          </div>

          <div className="package-form-row">
            <div className="package-form-group half">
              <label>Start Date</label>
              <input type="date" name="date_start" value={formData.date_start?.String || ''} onChange={handleChange} />
            </div>
            <div className="package-form-group half">
              <label>End Date</label>
              <input type="date" name="date_end" value={formData.date_end?.String || ''} onChange={handleChange} />
            </div>
          </div>

          <div className="package-form-row">
            <div className="package-form-group half">
              <label>Weight (kg)</label>
              <input type="number" step="0.01" name="total_weight" value={formData.total_weight} onChange={handleChange} />
            </div>
            <div className="package-form-group half">
              <label>Volume (m³)</label>
              <input type="number" step="0.01" name="total_volume" value={formData.total_volume} onChange={handleChange} />
            </div>
          </div>

          <div className="package-form-row">
            <div className="package-form-group half">
              <label>Status <span className="required">*</span></label>
              <select name="status" value={formData.status} onChange={handleChange} required>
                <option value="Pending">Pending</option>
                <option value="In Transit">In Transit</option>
                <option value="Delivered">Delivered</option>
              </select>
            </div>
            <div className="package-form-group half">
              <label>Courier ID</label>
              <input type="number" name="courier_id" value={formData.courier_id?.Int64 || ''} onChange={handleChange} placeholder="e.g. 1 or 2" />
            </div>
          </div>

          <div className="package-modal-actions">
            <button type="submit" className="btn-package-submit">{editData ? 'Save Changes' : 'Add Package'}</button>
            <button type="button" className="btn-package-cancel" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}
